import { EnvService } from "@/services/Environment/Environment";
import { File as SpotFile } from "@/types";

const MAX_FILE_SIZE = 31457280;

export const getMaxFileSize = () => {
  const maxFileSizeBytes = EnvService.getVar("MAX_UPLOAD_FILE_SIZE_BYTES");
  try {
    return maxFileSizeBytes && parseInt(maxFileSizeBytes) > 0
      ? parseInt(maxFileSizeBytes)
      : MAX_FILE_SIZE;
  } catch (e) {
    return MAX_FILE_SIZE;
  }
};

export enum FileTypes {
  "IMAGE" = "IMAGE",
  "JPEG" = "JPEG",
  "PNG" = "PNG",
  "TIFF" = "TIFF",
  "HEIC" = "HEIC",
  "AUDIO" = "AUDIO",
  "PDF" = "PDF",
  "CSV" = "CSV",
  "JSON" = "JSON",
  "MARKDOWN" = "MARKDOWN",
  "TXT" = "TXT",
  "ZIP" = "ZIP",
  "TAR" = "TAR",
  "GZIP" = "GZIP",
  "PPTX" = "PPTX",
  "PPT" = "PPT",
  "DOCX" = "DOCX",
  "DOC" = "DOC",
  "XLSX" = "XLSX",
  "XLS" = "XLS",
  "XML" = "XML",
  "HTML" = "HTML",
  "RTF" = "RTF",
  "EMAIL" = "EMAIL",
}

type FileTypeConfig = {
  extensions: string[];
  mimeTypes: string[];
  label: string;
  image: string;
};

const FILE_TYPE_CONFIGS: Record<FileTypes, FileTypeConfig> = {
  [FileTypes.IMAGE]: {
    extensions: [".jpg", ".jpeg", ".png", ".gif", ".tiff", ".heic", ".heif"],
    mimeTypes: [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/tiff",
      "image/svg+xml",
      "image/heic",
      "image/heif",
    ],
    label: "Image",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/IMG.svg",
  },
  [FileTypes.TIFF]: {
    extensions: [".tiff"],
    mimeTypes: ["image/tiff"],
    label: "TIFF",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/IMG.svg",
  },
  [FileTypes.JPEG]: {
    extensions: [".jpg", ".jpeg"],
    mimeTypes: ["image/jpeg"],
    label: "JPEG",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/IMG.svg",
  },
  [FileTypes.PNG]: {
    extensions: [".png"],
    mimeTypes: ["image/png"],
    label: "PNG",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/IMG.svg",
  },
  [FileTypes.HEIC]: {
    extensions: [".heic", ".heif"],
    mimeTypes: ["image/heic", "image/heif"],
    label: "HEIC",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/IMG.svg",
  },
  [FileTypes.AUDIO]: {
    extensions: [".mp3", ".wav", ".midi", ".webm", ".m4a"],
    mimeTypes: [
      "audio/mpeg",
      "audio/wav",
      "audio/midi",
      "audio/webm",
      "audio/mp4",
    ],
    label: "Audio",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/audio.svg",
  },
  [FileTypes.PDF]: {
    extensions: [".pdf"],
    mimeTypes: ["application/pdf"],
    label: "PDF",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/PDF.svg",
  },
  [FileTypes.CSV]: {
    extensions: [".csv"],
    mimeTypes: ["text/csv"],
    label: "CSV",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Excel.svg",
  },
  [FileTypes.JSON]: {
    extensions: [".json", ".nx"],
    mimeTypes: ["application/json"],
    label: "JSON",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Text.svg",
  },
  [FileTypes.MARKDOWN]: {
    extensions: [".md", ".markdown"],
    mimeTypes: ["text/markdown"],
    label: "Markdown",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Text.svg",
  },
  [FileTypes.TXT]: {
    extensions: [".txt"],
    mimeTypes: ["text/plain"],
    label: "Text",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Text.svg",
  },
  [FileTypes.ZIP]: {
    extensions: [".zip"],
    mimeTypes: ["application/zip", "application/x-zip-compressed"],
    label: "ZIP",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Zip.svg",
  },
  [FileTypes.TAR]: {
    extensions: [".tar"],
    mimeTypes: ["application/x-tar"],
    label: "TAR",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Zip.svg",
  },
  [FileTypes.GZIP]: {
    extensions: [".gz", ".tar.gz", ".tgz"],
    mimeTypes: ["application/gzip", "application/x-gzip"],
    label: "GZIP",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Zip.svg",
  },
  [FileTypes.PPTX]: {
    extensions: [".pptx"],
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ],
    label: "PowerPoint",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Presentation.svg",
  },
  [FileTypes.PPT]: {
    extensions: [".ppt"],
    mimeTypes: ["application/vnd.ms-powerpoint"],
    label: "PowerPoint",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Presentation.svg",
  },
  [FileTypes.DOCX]: {
    extensions: [".docx"],
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    label: "Word",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Word.svg",
  },
  [FileTypes.DOC]: {
    extensions: [".doc"],
    mimeTypes: ["application/msword"],
    label: "Word",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Word.svg",
  },
  [FileTypes.XLSX]: {
    extensions: [".xlsx"],
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
    label: "Excel",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Excel.svg",
  },
  [FileTypes.XLS]: {
    extensions: [".xls"],
    mimeTypes: ["application/vnd.ms-excel"],
    label: "Excel",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Excel.svg",
  },
  [FileTypes.XML]: {
    extensions: [".xml"],
    mimeTypes: ["application/xml", "text/xml"],
    label: "XML",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Text.svg",
  },
  [FileTypes.HTML]: {
    extensions: [".html", ".htm"],
    mimeTypes: ["text/html"],
    label: "HTML",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/nodes/globe.svg",
  },
  [FileTypes.RTF]: {
    extensions: [".rtf"],
    mimeTypes: ["application/rtf"],
    label: "RTF",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Text.svg",
  },
  [FileTypes.EMAIL]: {
    extensions: [".eml"],
    mimeTypes: ["message/rfc822"],
    label: "Email",
    image:
      "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Text.svg",
  },
};

const extensionToConfig = new Map<string, FileTypeConfig>();
const mimeTypeToConfig = new Map<string, FileTypeConfig>();

for (const config of Object.values(FILE_TYPE_CONFIGS)) {
  for (const ext of config.extensions) {
    extensionToConfig.set(ext.toLowerCase(), config);
  }
  for (const mime of config.mimeTypes) {
    mimeTypeToConfig.set(mime.toLowerCase(), config);
  }
}

// A spreadsheet can only be imported as a table, so the import entry points
// route on this rather than on which list page is open.
export const isSpreadsheet = (fileName: string): boolean =>
  /\.(csv|xlsx|xls)$/i.test(fileName.trim());

export function getMimeType(fileName: string): string[] {
  const extension = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();
  const config = extensionToConfig.get(extension);
  return config?.mimeTypes || ["application/octet-stream"];
}

export function getFileType(fileName: string): string {
  const extension = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();
  const config = extensionToConfig.get(extension);
  return config?.label || "File";
}

export function getFileImage(fileName: string): string {
  const extension = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();
  const config = extensionToConfig.get(extension);
  return (
    config?.image ||
    "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Text.svg"
  );
}

export function getFileTypeLabel(fileType: FileTypes): string {
  const config = FILE_TYPE_CONFIGS[fileType];
  return config?.label || "File";
}

export function getFileTypeImage(fileType: FileTypes): string {
  const config = FILE_TYPE_CONFIGS[fileType];
  return (
    config?.image ||
    "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Text.svg"
  );
}

export const isValidFileType = (
  file: File | SpotFile,
  allowed_file_types?: FileTypes[]
) => {
  if (!allowed_file_types) return true;

  const possibleTypes = getMimeType(file.name);
  const actualType = (file.type as string) || possibleTypes[0];

  for (const allowedType of allowed_file_types) {
    const config = FILE_TYPE_CONFIGS[allowedType as FileTypes];

    if (!config) {
      continue;
    }

    const isMatch = config.mimeTypes.some(
      (mimeType) => actualType === mimeType || possibleTypes.includes(mimeType)
    );

    if (isMatch) {
      return true;
    }
  }

  return false;
};

export const getAcceptAttributeValue = (
  allowed_file_types?: FileTypes[]
): string => {
  if (!allowed_file_types || allowed_file_types.length === 0) {
    return "*";
  }

  const extensions = new Set<string>();

  for (const fileType of allowed_file_types) {
    const config = FILE_TYPE_CONFIGS[fileType as FileTypes];
    if (config) {
      config.extensions.forEach((ext) => extensions.add(ext));
    }
  }

  return Array.from(extensions).join(",");
};

// Allowed FileTypes for an input/field of the given data type. A generic
// "File" accepts the broad document/image/audio set; typed Image / Audio
// inputs are narrowed to their own modality. Shared by the node fixed-value
// editor and the workflow runner input form so the two never drift.
export const fileTypesForDataType = (dataType?: string): FileTypes[] => {
  if (dataType === "Image") return [FileTypes.IMAGE];
  if (dataType === "Audio") return [FileTypes.AUDIO];
  return [
    FileTypes.CSV,
    FileTypes.TXT,
    FileTypes.PDF,
    FileTypes.JSON,
    FileTypes.DOCX,
    FileTypes.DOC,
    FileTypes.PPTX,
    FileTypes.PPT,
    FileTypes.JPEG,
    FileTypes.PNG,
    FileTypes.HEIC,
    FileTypes.AUDIO,
    FileTypes.MARKDOWN,
    FileTypes.ZIP,
    FileTypes.XML,
    FileTypes.RTF,
    FileTypes.EMAIL,
    FileTypes.XLSX,
    FileTypes.XLS,
  ];
};

export const areValidFileTypes = (
  files: FileList | null,
  allowed_file_types: FileTypes[] | undefined
): boolean => {
  if (!allowed_file_types) {
    return true;
  }
  if (!files || files.length === 0) {
    return false;
  }
  for (let i = 0; i < files.length; i++) {
    if (!isValidFileType(files[i], allowed_file_types)) {
      console.warn("Invalid file type", files[i]);
      return false;
    }
  }
  return true;
};
